// Decides whether a customer can see a menu item right now.
// The restaurant's actual hours always win: a holiday closure or an early close
// hides a scheduled item even on its scheduled day. Taking an item off (is_86)
// is checked separately by the callers and wins over everything.
// supabase/functions/stripe-checkout/index.ts holds the same rule for checkout.

import { useEffect, useState } from 'react'
import { BusinessHourRow, SpecialHourRow, effectiveHours, jsDayToDayOrder, nowInLA, parseTimeToMinutes } from './businessHours'
import { MenuItem, VisibilitySchedule } from '../types'

export function isItemShowingNow(
  item: Pick<MenuItem, 'visibility_schedule'>,
  rows: BusinessHourRow[],
  specials: SpecialHourRow[],
): boolean {
  const s = item.visibility_schedule
  if (!s) return true

  const { minutes, jsDay } = nowInLA()
  if (!s.days.includes(jsDay)) return false

  const today = effectiveHours(rows, specials, jsDayToDayOrder(jsDay), 0)
  if (today) {
    if (today.is_closed) return false
    const open = parseTimeToMinutes(today.open_time)
    const close = parseTimeToMinutes(today.close_time)
    if (open !== null && close !== null && (minutes < open || minutes >= close)) return false
  }

  if (s.mode === 'custom' && s.start !== undefined && s.end !== undefined) {
    return minutes >= s.start && minutes < s.end
  }
  return true
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function minutesToLabel(mins: number): string {
  const h24 = Math.floor(mins / 60)
  const m = mins % 60
  const h = h24 % 12 === 0 ? 12 : h24 % 12
  return `${h}${m ? ':' + String(m).padStart(2, '0') : ''} ${h24 < 12 ? 'AM' : 'PM'}`
}

/** Short label for the calendar button: "Tue" or "Mon, Wed" or "Every day". */
export function scheduleDaysLabel(s: VisibilitySchedule): string {
  if (s.days.length === 7) return 'Every day'
  return [...s.days].sort((a, b) => a - b).map(d => DAY_NAMES[d]).join(', ')
}

export function scheduleFullLabel(s: VisibilitySchedule): string {
  const when = s.mode === 'custom' && s.start !== undefined && s.end !== undefined
    ? `${minutesToLabel(s.start)} to ${minutesToLabel(s.end)}`
    : 'open to close'
  return `${scheduleDaysLabel(s)}, ${when}`
}

/** Re-renders once a minute so an open page drops or shows items on time. */
export function useMinuteTick(): void {
  const [, setTick] = useState(0)
  useEffect(() => {
    const t = window.setInterval(() => setTick(n => n + 1), 60_000)
    return () => window.clearInterval(t)
  }, [])
}
